import { useEffect, useState, useCallback } from 'react';
import { observer } from 'mobx-react-lite';
import { format } from 'date-fns';
import { Card } from '@/components/ui/card';
import MainLayout from '@/components/layouts/MainLayout';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from '@/components/ui/button';
import { AlertCircle } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { parseUserAgent } from '@/utils/userAgent';
import { axiosInstance } from '@/utils/axiosConfig';
import { useTranslation } from 'react-i18next';

interface LoginHistoryEntry {
  id: string;
  createdAt: string;
  ipAddress: string | null;
  userAgent: string | null;
}

interface PaginationData {
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

interface DeviceInfo {
  browser: string;
  deviceType: string;
  ipAddress: string;
}

interface ActiveSession {
  id: string;
  createdAt: string;
  expiresAt: string;
  lastActive: string;
  isCurrentSession: boolean;
  deviceInfo: DeviceInfo;
}

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

const SecurityPage = observer(() => {
  const { t } = useTranslation();
  const [loginHistory, setLoginHistory] = useState<LoginHistoryEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [pagination, setPagination] = useState<PaginationData | null>(null);
  const [activeSessions, setActiveSessions] = useState<ActiveSession[]>([]);
  const [sessionsLoading, setSessionsLoading] = useState(true);
  const [sessionsError, setSessionsError] = useState<string | null>(null);

  const fetchLoginHistory = useCallback(async (page: number, limit: number) => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(`${import.meta.env.VITE_API_URL}/auth/login-history?page=${page}&limit=${limit}`);
      setLoginHistory(response.data.data);
      setPagination(response.data.pagination);
      setError(null);
    } catch (error) {
      setError(t('security.failedToLoadLoginHistory'));
      console.error('Error fetching login history:', error);
    } finally {
      setLoading(false);
    }
  }, [t]);

  const fetchActiveSessions = useCallback(async () => {
    try {
      setSessionsLoading(true);
      const response = await axiosInstance.get(`${import.meta.env.VITE_API_URL}/auth/active-sessions`);
      setActiveSessions(response.data);
      setSessionsError(null);
    } catch (error) {
      setSessionsError(t('security.failedToLoadActiveSessions'));
      console.error('Error fetching active sessions:', error);
    } finally {
      setSessionsLoading(false);
    }
  }, [t]);

  const handleRevokeSession = async (sessionId: string) => {
    try {
      await axiosInstance.delete(`${import.meta.env.VITE_API_URL}/auth/sessions/${sessionId}`);
      await fetchActiveSessions(); // Refresh the list
    } catch (error) {
      console.error('Error revoking session:', error);
    }
  };

  const handleRevokeOtherSessions = async () => {
    try {
      await axiosInstance.delete(`${import.meta.env.VITE_API_URL}/auth/sessions`);
      await fetchActiveSessions(); // Refresh the list
    } catch (error) {
      console.error('Error revoking other sessions:', error);
    }
  };

  useEffect(() => {
    fetchLoginHistory(currentPage, pageSize);
    fetchActiveSessions();
  }, [currentPage, pageSize, fetchLoginHistory, fetchActiveSessions]);

  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
  };

  const handlePageSizeChange = (newSize: string) => {
    setPageSize(Number(newSize));
    setCurrentPage(1); // Reset to first page when changing page size
  };

  const renderTableContent = () => {
    if (loading) {
      return (
        <TableRow>
          <TableCell colSpan={4} className="text-center py-8">
            {t('security.loading')}
          </TableCell>
        </TableRow>
      );
    }

    if (error) {
      return (
        <TableRow>
          <TableCell colSpan={4} className="text-center py-8 text-red-500">
            {error}
          </TableCell>
        </TableRow>
      );
    }

    if (!loginHistory || loginHistory.length === 0) {
      return (
        <TableRow>
          <TableCell colSpan={4} className="text-center py-8">
            {t('security.noLoginHistoryAvailable')}
          </TableCell>
        </TableRow>
      );
    }

    return loginHistory.map(entry => {
      const { browser, deviceType } = parseUserAgent(entry.userAgent || '');
      const createdAt = new Date(entry.createdAt);
      
      return (
        <TableRow key={entry.id}>
          <TableCell className="whitespace-normal break-words">
            {!Number.isNaN(createdAt.getTime()) ? format(createdAt, 'PPpp') : t('security.unknown')}
          </TableCell>
          <TableCell className="whitespace-normal break-words">{entry.ipAddress || t('security.unknown')}</TableCell>
          <TableCell className="whitespace-normal break-words">{browser || t('security.unknown')}</TableCell>
          <TableCell className="whitespace-normal break-words">{deviceType || t('security.unknown')}</TableCell>
        </TableRow>
      );
    });
  };

  return (
    <MainLayout>
      <div className="px-4 max-w-full">
        <Card className="p-4 md:p-6 overflow-hidden">
          <div className="space-y-6">
            <div>
              <h2 className="text-lg font-semibold mb-4">{t('security.activeSessions')}</h2>
              {sessionsLoading ? (
                <div className="py-4">{t('security.loading')}</div>
              ) : sessionsError ? (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{sessionsError}</AlertDescription>
                </Alert>
              ) : (
                <>
                  <div className="overflow-x-auto -mx-4 px-4">
                    <Table className="min-w-full">
                      <TableHeader>
                        <TableRow>
                          <TableHead className="whitespace-nowrap">{t('security.browser')}</TableHead>
                          <TableHead className="whitespace-nowrap">{t('security.device')}</TableHead>
                          <TableHead className="hidden md:table-cell">{t('security.ipAddress')}</TableHead>
                          <TableHead className="hidden md:table-cell">{t('security.lastActive')}</TableHead>
                          <TableHead className="whitespace-nowrap">{t('security.actions')}</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {activeSessions.map((session) => {
                          const createdAt = new Date(session.createdAt);

                          return (
                            <TableRow key={session.id}>
                              <TableCell className="whitespace-normal break-words">
                                {session.deviceInfo?.browser || t('security.unknown')}
                              </TableCell>
                              <TableCell className="whitespace-normal break-words">
                                {session.deviceInfo?.deviceType || t('security.unknown')}
                              </TableCell>
                              <TableCell className="hidden md:table-cell whitespace-normal break-words">
                                {session.deviceInfo?.ipAddress || t('security.unknown')}
                              </TableCell>
                              <TableCell className="hidden md:table-cell whitespace-normal break-words">
                                {!Number.isNaN(createdAt.getTime()) ? format(createdAt, 'PPpp') : t('security.unknown')}
                              </TableCell>
                              <TableCell>
                                {session.isCurrentSession ? (
                                  <span className="text-sm text-gray-500">{t('security.currentSession')}</span>
                                ) : (
                                  <Button
                                    variant="destructive"
                                    size="sm"
                                    onClick={() => handleRevokeSession(session.id)}
                                    className="whitespace-nowrap"
                                  >
                                    {t('security.signOut')}
                                  </Button>
                                )}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </div>
                  {activeSessions.length > 1 && (
                    <div className="mt-4">
                      <Button
                        variant="outline"
                        onClick={handleRevokeOtherSessions}
                        className="w-full md:w-auto"
                      >
                        {t('security.signOutOtherSessions')}
                      </Button>
                    </div>
                  )}
                </>
              )}
            </div>

            <div className="border-t pt-6">
              <h2 className="text-lg font-semibold mb-4">{t('security.loginHistory')}</h2>
              <div className="flex justify-between items-center mb-4 flex-wrap gap-2">
                <Select
                  value={pageSize.toString()}
                  onValueChange={handlePageSizeChange}
                >
                  <SelectTrigger className="w-[150px]">
                    <SelectValue placeholder={t('security.loading')} />
                  </SelectTrigger>
                  <SelectContent>
                    {PAGE_SIZE_OPTIONS.map((size) => (
                      <SelectItem key={size} value={size.toString()}>
                        {t('security.itemsPerPage', { size })}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="overflow-x-auto -mx-4 px-4">
                <Table className="min-w-full">
                  <TableHeader>
                    <TableRow>
                      <TableHead className="whitespace-nowrap">{t('security.date')}</TableHead>
                      <TableHead className="hidden md:table-cell">{t('security.ipAddress')}</TableHead>
                      <TableHead className="whitespace-nowrap">{t('security.browser')}</TableHead>
                      <TableHead className="whitespace-nowrap">{t('security.device')}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {renderTableContent()}
                  </TableBody>
                </Table>
              </div>

              {pagination && !loading && !error && (
                <div className="flex flex-col md:flex-row justify-between items-center mt-4 gap-4">
                  <div className="text-sm text-gray-500 w-full text-center md:text-left">
                    {t('security.showingEntries', {
                      start: ((currentPage - 1) * pageSize) + 1,
                      end: Math.min(currentPage * pageSize, pagination.total),
                      total: pagination.total
                    })}
                  </div>
                  <div className="flex gap-2 w-full md:w-auto justify-center">
                    <Button
                      variant="outline"
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                      className="flex-1 md:flex-none"
                    >
                      {t('security.previous')}
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === pagination.totalPages}
                      className="flex-1 md:flex-none"
                    >
                      {t('security.next')}
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </Card>
      </div>
    </MainLayout>
  );
});

export default SecurityPage; 